import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthContext';

export function useTimeEntries() {
  const { authFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch('/api/timeEntries');
      if (!res.ok) throw new Error('Failed to fetch time entries');
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : (data.entries || []));
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  function addEntry(newEntry) {
    setEntries(prev => [...prev, newEntry]);
  }

  function updateEntry(updatedEntry) {
    setEntries(prev =>
      prev.map(entry => entry._id === updatedEntry._id ? updatedEntry : entry)
    );
  }

  function deleteEntry(id) {
    setEntries(prev => prev.filter(entry => entry._id !== id));
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const res = await authFetch(`/api/timeEntries/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete entry');
      deleteEntry(id);
      alert('✅ Entry deleted!');
    } catch (err) {
      console.error('Error deleting entry:', err);
      alert('❌ Error deleting entry');
    }
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    if (!editingEntry) return;
    try {
      const res = await authFetch(`/api/timeEntries/${editingEntry._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingEntry),
      });
      if (!res.ok) throw new Error('Failed to update entry');
      const updatedEntry = await res.json();
      updateEntry(updatedEntry);
      setEditingEntry(null);
      alert('✅ Entry updated!');
    } catch (err) {
      console.error('Error updating entry:', err);
      alert('❌ Error updating entry');
    }
  }

  // Submit a new time entry — saves as pending, awaits superadmin approval
  async function handleSubmit(formData, resetCallback) {
    setError(null);
    setSubmitStatus(null);
    setLoading(true);
    try {
      const res = await authFetch('/api/timeEntries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        let errorMessage = data.message || 'Failed to submit entry';
        if (data.errors?.length > 0) {
          errorMessage += ':\n\n' + data.errors.join('\n');
        }
        setError(errorMessage);
        setSubmitStatus('error');
        return;
      }

      resetCallback();
      setSubmitStatus('success');
      fetchEntries();
    } catch (err) {
      setError(err.message || 'Error submitting data');
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  }

  // Approve a pending entry (superadmin only)
  async function handleApprove(id) {
    try {
      const res = await authFetch(`/api/timeEntries/${id}/approve`, { method: 'PATCH' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to approve entry');
      }
      const data = await res.json();
      updateEntry(data.entry);
    } catch (err) {
      console.error('Approve error:', err);
      alert('❌ ' + err.message);
    }
  }

  // Reject a pending entry with a required reason (superadmin only)
  async function handleReject(id, reviewNotes) {
    try {
      const res = await authFetch(`/api/timeEntries/${id}/reject`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewNotes }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to reject entry');
      }
      const data = await res.json();
      updateEntry(data.entry);
    } catch (err) {
      console.error('Reject error:', err);
      alert('❌ ' + err.message);
    }
  }

  return {
    entries,
    loading,
    error,
    submitStatus,
    editingEntry,
    setEditingEntry,
    fetchEntries,
    addEntry,
    updateEntry,
    deleteEntry,
    handleDelete,
    handleEditSubmit,
    setEntries,
    handleSubmit,
    handleApprove,
    handleReject,
  };
}